import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onMounted, ref } from 'vue';
import { useMounted } from '@vueuse/core';

import { useExperimentStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useArbitrage } from '@/helpers/composables/useArbitrage';

import { usePromotion } from '@/helpers/composables/usePromotion';

import { OfferLocation } from '@/helpers/offer-helper';
import type { Offer } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { appleQualityTvCountries, qualityTVProviderConfig } from '@/components/buybox/QualityTvRow/data';
import BuyBoxFreeTrialSwiperOS from '@/components/buybox/BuyBoxFreeTrialSwiperOS.vue';
import { freeTrialSwiperRules } from '@/helpers/promotion-helper';
import { AppleChannelsExpName, AppleChannelsExpVariants } from '@/components/experiments/AppleChannelsExp';

import { useSingleQtvExp } from '../experiments/SingleQtvExp/composables/useSingleQtvExp';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxPromotionRowNew',
  props: {
    offers: null,
    showFreeTrial: { type: Boolean },
    isFreeBuyBox: { type: Boolean },
    title: null,
    additionalContexts: null,
    trackingContexts: null,
    seasonNumber: null,
    episodeNumber: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: TitleOfferFragment | null;
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offers: Offer[];
	showFreeTrial: boolean;
	isFreeBuyBox: boolean;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
};

const PromotionRowNew = () => import('@/components/buybox-new/PromotionRowNew.vue');
const BuyBoxQualityTVNew = () => import('@/components/buybox-new/qualityTV/BuyBoxQualityTVNew.vue');

const SingleQTV = () => import('../experiments/SingleQtvExp/components/SingleQtvContainer.vue');


const { isProUser } = useUserStore();
const { isArbitrating } = useArbitrage();
const { country } = useLanguageStore();
const isMounted = useMounted();
const { activeVariantsWithControlGroup } = useExperimentStore();
const { isLoggedIn } = useUserStore();

// SINGLE_QTV_EXP
const { expActiveVariant } = useSingleQtvExp();

const isMountedAfterTick = ref(false);

onMounted(async () => {
	await nextTick();
	isMountedAfterTick.value = true;
});

const showSingleQTV = computed(() => {
	return (
		isMountedAfterTick.value &&
		!inPromotionCatalogue.value &&
		!!expActiveVariant.value &&
		expActiveVariant.effect !== 'control'
	);
});

// SINGLE_QTV_EXP



// APPLE_CHANNELS_EXP

const appleChannelsExpVariant = computed(() => {
	if (!isMounted.value || !['GB', 'AU', 'CA'].includes(country.value) || isLoggedIn.value || isArbitrating.value)
		return null;

	return (activeVariantsWithControlGroup.value[AppleChannelsExpName] as AppleChannelsExpVariants | undefined) ?? null;
});

const hardcodedFallbackOfferId = computed(() => {
	if (appleChannelsExpVariant.value && appleChannelsExpVariant.value !== 'control') return promotionPackageId.value;
	return undefined;
});
// APPLE_CHANNELS_EXP

const { promotionOffer, catalogueItem, inPromotionCatalogue } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
	// APPLE_CHANNELS_EXP
	hardcodedFallbackOfferId,
	// APPLE_CHANNELS_EXP
});

const showPromotionRowWhenArbitrating = computed(
	() => isArbitrating.value && 350 === promotionOffer.value?.package?.packageId
);

const promotionPackageId = computed(() => {
	// APPLE_CHANNELS_EXP
	if (appleChannelsExpVariant.value && appleChannelsExpVariant.value !== 'control')
		return country.value === 'GB' ? 642 : 1854;
	// APPLE_CHANNELS_EXP

	return promotionOffer.value?.package?.packageId;
});

// APPLE_CHANNELS_EXP
const isAppleChannelsExpQTV = computed(() => {
	if (appleChannelsExpVariant.value && appleChannelsExpVariant.value !== 'control')
		return !props.offers.some(offer => offer.package.packageId === promotionPackageId.value);

	return false;
});
// APPLE_CHANNELS_EXP

const isQualityTvCountry = computed(() => {
	if (!promotionOffer.value) return false;

	if (promotionOffer.value.package.packageId === 2) return true;

	return (
		promotionOffer.value.package.packageId === 350 &&
		!inPromotionCatalogue.value &&
		appleQualityTvCountries.includes(country.value)
	);
});

const freeTrialActive = computed(() => {
	return (
		!isProUser.value &&
		(catalogueItem.value?.placement === 'ft' || (!catalogueItem.value?.placement && props.showFreeTrial))
	);
});

const hideTVODQTV = computed(
	() =>
		catalogueItem.value?.placement === 'qtv' &&
		qualityTVProviderConfig[catalogueItem.value.packageId].type !== 'FREE' &&
		props.isFreeBuyBox
);

const qualityTvActive = computed(() => {
	return (
		!isProUser.value &&
		(!isMounted.value || !showPromotionRowWhenArbitrating.value) &&
		// either placement is overwritten by yo, or the programmatic decision checks
		((catalogueItem.value?.placement === 'qtv' && qualityTVProviderConfig[catalogueItem.value.packageId]) ||
			(!catalogueItem.value?.placement && isQualityTvCountry.value) ||
			// APPLE_CHANNELS_EXP
			isAppleChannelsExpQTV.value) &&
		// APPLE_CHANNELS_EXP
		// Itunes TVOD QTV is not a Free Trial, cannot be shown when filter is Free.
		!hideTVODQTV.value
	);
});

const freeTrialSwiperActive = computed(() => {
	if (!freeTrialSwiperRules) return false;
	return Object.keys(freeTrialSwiperRules).includes(country.value);
});

function onClickOut(payload: {
	buyboxOffer: TitleOfferFragment | null;
	offerLocation: OfferLocation.FREE_TRIAL;
	row: HTMLElement | null;
}) {
	emit('clickOut', {
		buyboxOffer: payload.buyboxOffer,
		offerLocation: payload.offerLocation,
		row: payload.row,
	});
}

return { __sfc: true,PromotionRowNew, BuyBoxQualityTVNew, SingleQTV, props, isProUser, isArbitrating, country, isMounted, activeVariantsWithControlGroup, isLoggedIn, expActiveVariant, isMountedAfterTick, showSingleQTV, emit, appleChannelsExpVariant, hardcodedFallbackOfferId, promotionOffer, catalogueItem, inPromotionCatalogue, showPromotionRowWhenArbitrating, promotionPackageId, isAppleChannelsExpQTV, isQualityTvCountry, freeTrialActive, hideTVODQTV, qualityTvActive, freeTrialSwiperActive, onClickOut, BuyBoxFreeTrialSwiperOS }
}

})